import * as Sentry from "@sentry/react";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";
import { useEffect } from "react";

const dsn = import.meta.env.VITE_SENTRY_DSN;
const isProduction = import.meta.env.MODE === 'production';

const toNumber = (value: string | undefined, fallback: number) => {
  if (!value?.length) {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

// Only initialize Sentry in production
if (dsn && isProduction) {
  const tracesSampleRate = toNumber(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0);
  const sessionSampleRate = toNumber(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE, 0);
  const errorSampleRate = toNumber(import.meta.env.VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE, 1);

  const integrations: Array<
    ReturnType<typeof Sentry.reactRouterV6BrowserTracingIntegration> | ReturnType<typeof Sentry.replayIntegration>
  > = [];

  if (tracesSampleRate > 0) {
    integrations.push(
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      })
    );
  }

  if (sessionSampleRate > 0 || errorSampleRate > 0) {
    integrations.push(
      Sentry.replayIntegration({
        sessionSampleRate,
        errorSampleRate,
      })
    );
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    integrations,
    tracesSampleRate,
  });
}

type SentryUserContext = {
  userId: string;
  email?: string;
  name?: string;
  organization?: { id: string; name: string; code: string } | null;
  role?: string;
};

/**
 * Rattache les erreurs remontées au compte et au salon connectés : sans cela,
 * Sentry affiche « 0 utilisateur impacté » et le salon concerné reste inconnu.
 * Appelée par OrganizationContext à chaque changement de session ou de salon ;
 * `null` à la déconnexion. Sans effet hors production (Sentry non initialisé).
 */
export function setSentryUserContext(ctx: SentryUserContext | null) {
  if (!ctx) {
    Sentry.setUser(null);
    Sentry.setTags({ salon: undefined, salon_code: undefined, salon_id: undefined, role: undefined });
    return;
  }

  Sentry.setUser({ id: ctx.userId, email: ctx.email, username: ctx.name });
  Sentry.setTags({
    salon: ctx.organization?.name,
    salon_code: ctx.organization?.code,
    salon_id: ctx.organization?.id,
    role: ctx.role,
  });
}
